export type AppMode =
  | 'dialogue'
  | 'letstalk'
  | 'user-management'
  | 'client-profile';

export type ChatMode = 'clients' | 'actors' | 'custom-actors';

/**
 * Components can change their hue based on this property.
 */
export type AppHue = 'assistant' | 'client';

export type AgentType = 'assistant' | 'actor';

export enum ChatType {
  Actor = 'actor',
  Client = 'client',
}

export type ActorType =
  | 'suicidal_medium'
  | 'suicidal_high'
  | 'grieving'
  | 'stressed'
  | 'lonely'
  | 'health_concern'
  | 'heartbroken'
  | 'addicted'
  | 'traumatised'
  | 'paranoid'
  | 'abused'
  | 'parent'
  | 'body_image'
  | 'bored'
  | 'elderly_SOS'
  | 'grief_SOS'
  | 'alcoholism_SOS'
  | 'friendship_rejection_SOS'
  | 'concerned_son_SOS'
  | 'concerned_girlfriend_SOS'
  | 'non_binary_SOS'
  | 'concerned_friend_SOS'
  | 'harassment_SOS'
  | 'overwork_SOS'
  | 'delusional_SOS'
  | 'borderline_PD_SOS'
  | 'isolation_SOS'
  | 'heartbroken_SOS'
  | 'unemployment_SOS'
  | 'caregiver_SOS'
  | 'transgender_SOS'
  | 'teacher_SOS'
  | 'mindline_alice'
  | 'mindline_amos'
  | 'mindline_dwight';

export type PromptType =
  | 'direct'
  | 'summarize'
  | 'summarize_post'
  | 'generate_directions'
  | 'reasons_for_living'
  | 'recommend_resource'
  | 'generate_response'
  | 'generate_response_conversational'
  | 'generate_wrap_up'
  | 'generate_lets_talk_response_befriender'
  | 'generate_lets_talk_response_therapist'
  | 'generate_problem_identification'
  | 'generate_attempted_solutions'
  | 'generate_desired_outcome'
  | 'generate_interventions'
  | 'generate_C3P_guide'
  | 'empathetic_rewrite'
  | 'generate_feedback_CCSR'
  | 'generate_feedback_CT_supervision'
  | 'generate_feedback_FIST'
  | 'generate_feedback_TES'
  | 'generate_response_trained';

export type ModelTag =
  | 'o1-mini'
  | 'o3-mini'
  | 'gpt-5.2'
  | 'gpt-5.2-enabled'
  | 'gpt-4o'
  | 'gpt-4o-mini'
  | 'ft:gpt-4o-mini-2024-07-18:moht:sos-sft-2a:BBF6ERzt'
  | 'ft:gpt-4.1-mini-2025-04-14:moht:sos-sft-mini4-1-realdata2-lessof:BsSPSrj8:ckpt-step-815'
  | 'gpt-3.5-turbo'
  | 'llama3-8b-instruct'
  | 'llama3-70b-instruct'
  | 'mistral-7b-instruct'
  | 'mixtral-8x7b-instruct'
  | 'mistral-large'
  | 'claude-4.5-sonnet'
  | 'aisingapore/Llama-SEA-LION-v3.5-8B-R';

export type RoleType =
  | 'admin'
  | 'user_new'
  | 'user_sos'
  | 'user_public'
  | 'user_lets_talk'
  | 'user_trainee'
  | 'user_scs';

export type GenderType = 'female' | 'male' | 'nonbinary' | 'undisclosed';

export type SuicideLabel = 'LABEL_0' | 'LABEL_1';

export interface ApiError {
  status_code: number;
  detail: string;
}

export type MessageRole = 'assistant' | 'user';

export interface BaseMessage {
  role: MessageRole;
  content: string;
}

export interface ChatMessage extends BaseMessage {
  messageId: string;
  createdAt: string;
  isUnread?: boolean;
  hasAutoReply?: boolean;
  modelTag?: string;
  reasoningResponse?: string;
}

export interface ChatMessageWithClient extends ChatMessage {
  contentSentiment?: number;
  historySentiment?: number;
  contentSuicideLabel?: SuicideLabel;
  contentSuicideScore?: number;
  sentimentProcessing?: boolean;
  suicidalityProcessing?: boolean;
}

export interface ChatMessageWithAssistant extends ChatMessage {
  promptType: PromptType;
}

export interface Chat {
  chatId: string;
  messagesWithClient: { [messageId: string]: ChatMessageWithClient };
  messagesWithAssistant: { [messageId: string]: ChatMessageWithAssistant };
}

export interface ChatInput {
  inputWithClient: string;
  inputWithAssistant: string;
}

export type ChatMap = { [userId: string]: Chat };

export type ChatInputMap = { [userId: string]: ChatInput };

export interface Client {
  userType: 'actor' | 'client';
  userId: string;
  username?: string;
  friendlyName?: string;
  isArchived?: boolean;
  level?: string;
  name?: string;
  isCustomActor?: boolean;
  actorId?: string;
}

export interface ClientProfile {
  age: number | null;
  gender: string | null;
  address: string | null;
  ethnicity: string | null;
  language_preferred: string | null;
  ses: string | null;
  religion: string | null;
  occupation: string | null;
  accommodation: string | null;
  relationship_status: string | null;
  id: string;
  created_at: string;
  updated_at: string;
  summary?: string;
  level?: string;
}

export type ClientMap = { [userId: string]: Client };

export interface Actor extends Client {
  userType: 'actor';
  prompt?: string;
  local_time_start?: string;
  owner?: string;
}

export type ActorMap = { [userId: string]: Actor };

export type MessageScoreMap = { [messageId: string]: number };

export interface PayloadBase {
  userId: string;
  payload: string;
}

export interface PayloadMessage extends PayloadBase {
  messageId: string;
  timestamp?: string;
  user?: string;
  chatType?: ChatType;
}

export interface PayloadMessageAssistant extends PayloadMessage {
  promptType?: PromptType;
  modelTag?: ModelTag;
}

export type RiskType = 'Neutral' | 'Low' | 'Medium' | 'High';

export type RiskColor = 'default' | 'secondary' | 'warning' | 'danger';

export interface PayloadRisk {
  key: string;
  risk?: RiskType;
  reasoning_trace?: string | null;
  riskProcessing?: boolean;
  clientMessages?: number;
}

export interface PayloadMessageSentiments {
  userId: string;
  messageId: string;
  contentSentiment?: number;
  historySentiment?: number;
  sentimentProcessing?: boolean;
  contentSuicideLabel?: SuicideLabel;
  contentSuicideScore?: number;
  suicidalityProcessing?: boolean;
}

export interface SettingsFeature {
  key: PromptType;
  label: string;
  description?: string;
  isEnabled: boolean;
  isAutoRun: boolean;
  isAutoRunnable?: boolean;
}

export interface SettingsAgentConfig {
  key: AgentType;
  label: string;
  modelTag: ModelTag;
  proposeResponseModelTag: ModelTag;
}

export interface SettingsAnalytics {
  showTrafficLight?: boolean;
  autoTrafficLight?: boolean;
  reasoning?: boolean;
  autoEvaluateMessageRisk?: boolean;
  autoEvaluateSentiment?: boolean;
  evaluateSuicidality?: boolean;
}

export interface SettingsDeveloper {
  maskClientMessage?: boolean;
  includeExplanationState?: boolean;
  showMessageModelTag?: boolean;
  showSelfPlayOptions?: boolean;
}

export interface SettingsActor extends SettingsAgentConfig {
  typingSimulated?: boolean;
  automaticReplies?: boolean;
  actorDetails?: boolean;
  actorProfile?: boolean;
}

export interface User {
  id: string;
  username: string;
  role: RoleType;
  name?: string;
  createdAt?: string;
  updatedAt?: string;
  actor?: string[];
}

export type UserMap = { [id: string]: User };

export interface UserAuth {
  username: string;
  password: string;
}

export interface UserRegister extends UserAuth {
  name: string;
}

export interface WebSocketMessageDto {
  message_id: string;
  chat_id: string;
  content: string;
  is_user: boolean;
  username?: string;
  first_name?: string;
  created_at?: string;
}

export interface BotDetails {
  username: string;
  friendly_name: string;
  link: string;
}

export interface TraineeBubble {
  chatId: string;
  messageId: string;
  role: string;
  name: string;
  content: string;
  original_created_at: string;
  isUnread: boolean;
}

export interface TraineeBubbleInfo {
  chatId: string;
  owner: string;
  chatName: string;
  feedback: string;
  reviewer: string;
  status: string;
  stage: string;
  notes: string;
}
